import type { CSSProperties, HTMLAttributes } from "react";
import { cn } from "@/shared/lib/utils";

/**
 * Font-size (px) drives the cluster: each dot is `0.55em`, gaps scale with it.
 * Roomier than the meatballs loader — sized for search inputs and page /
 * big-component loading where horizontal space is available.
 */
const fontSizes = {
	sm: 12,
	default: 16,
	md: 20,
	lg: 26,
	xl: 34,
} as const;

const speeds = {
	slow: "1.6s",
	normal: "1.2s",
	fast: "0.9s",
} as const;

export interface DotsLoaderProps
	extends Omit<HTMLAttributes<HTMLSpanElement>, "children"> {
	size?: keyof typeof fontSizes;
	speed?: keyof typeof speeds;
}

/**
 * Roomy loading indicator: three dots bouncing in a travelling wave. Color
 * inherits from the parent's `text-*` via `bg-current`. Structural classes come
 * last in `cn` so consumer width/height utilities can't clip the cluster — pass
 * `text-*` for color, margins for spacing, and the `size` prop to scale.
 *
 * @example
 * <DotsLoader />
 * <DotsLoader size="lg" className="text-primary" />
 */
export function DotsLoader({
	size = "default",
	speed = "normal",
	className,
	style,
	...props
}: DotsLoaderProps) {
	return (
		<span
			aria-label="Loading"
			className={cn(
				className,
				"inline-flex w-fit items-center justify-center gap-[0.4em] align-middle leading-none",
			)}
			data-slot="spinner"
			role="status"
			style={{ fontSize: fontSizes[size], ...style } as CSSProperties}
			{...props}
		>
			{[0, 1, 2].map((i) => (
				<span
					aria-hidden="true"
					className="inline-block size-[0.55em] animate-dots-bounce rounded-full bg-current"
					key={i}
					style={
						{
							"--dots-duration": speeds[speed],
							"--dots-delay": `${i * 160}ms`,
						} as CSSProperties
					}
				/>
			))}
		</span>
	);
}
